import { Button, IconButton } from '@chakra-ui/button';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { useClipboard, useDisclosure } from '@chakra-ui/hooks';
import Icon from '@chakra-ui/icon';
import { Input, InputGroup, InputRightElement } from '@chakra-ui/input';
import { Flex, Heading, Stack, Text } from '@chakra-ui/layout';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/modal';
import { Radio, RadioGroup } from '@chakra-ui/radio';
import { Tooltip } from '@chakra-ui/tooltip';
import firebase from 'firebase';
import moment from 'moment';
import React, { useState } from 'react';
import { Check, Clipboard, Zap } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { uuid } from '../../../utils/uuid';

interface AdminCourseModalQuickstartProps {}

export const AdminCourseModalQuickstart: React.FC<AdminCourseModalQuickstartProps> =
  () => {
    const [id] = useState(uuid());
    const history = useHistory();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const form = useForm<{
      duration: string;
      privacy: 'private' | 'public';
      title: string;
    }>({
      defaultValues: { privacy: 'public', duration: '60', title: '' },
    });

    const privateUrl = `/private-course/${id}/Privater%20Raum`;
    const fullPrivateUrl = `${window.location.origin}${privateUrl}`;
    const { hasCopied, onCopy } = useClipboard(fullPrivateUrl);

    const onSubmit = form.handleSubmit((values) => {
      const title = values.title !== '' ? values.title : 'Spontaner Live-Kurs';
      if (values.privacy === 'private') {
        history.push(privateUrl);
      } else {
        firebase
          .firestore()
          .collection('courses')
          .add({
            start: moment().toJSON(),
            title,
            description: '',
            images: {
              cover: '',
              coverThumb: '',
            },
            duration: +values.duration,
            trainer: {
              ids: [''],
            },
            isOnline: true,
            recurrence: {
              isRecurring: false,
              daysOfWeek: new Array(7).fill(false),
              weekInterval: 1,
            },
            courseCategoryId: '',
            created: firebase.firestore.FieldValue.serverTimestamp(),
            lastModified: firebase.firestore.FieldValue.serverTimestamp(),
          })
          .then((ref) =>
            history.push(
              `/public-course/${ref.id}/${encodeURIComponent(title)}`
            )
          );
      }
    });

    return (
      <>
        <Button
          mb={6}
          borderRadius="xl"
          isFullWidth={true}
          leftIcon={<Icon as={Zap} />}
          onClick={onOpen}
        >
          Schnellstart
        </Button>
        <Modal size="xl" isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent boxShadow="xl" borderRadius="xl" p={6}>
            <Heading size="md" mb={6}>
              Schnellstart
            </Heading>
            <ModalCloseButton />
            <form onSubmit={onSubmit}>
              <FormControl mb={4}>
                <FormLabel>Für wen ist der Kurs?</FormLabel>
                <RadioGroup colorScheme="brand" defaultValue="public">
                  <Stack direction="row">
                    <Radio value="public" {...form.register('privacy')}>
                      für alle
                    </Radio>
                    <Radio value="private" {...form.register('privacy')}>
                      privat
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
              {form.watch('privacy') === 'private' ? (
                <Flex direction="column" mb={6}>
                  <Text size="sm" mb={3}>
                    Wenn du den Link kopiert hast, kannst du ihn mit den
                    Teilnehmern teilen. Klicke dann auf Kurs starten.
                  </Text>
                  <Flex align="center">
                    <Text mr={2} whiteSpace="nowrap">
                      Link zum Kopieren
                    </Text>
                    <InputGroup>
                      <Input
                        focusBorderColor="brand"
                        value={fullPrivateUrl}
                        readOnly={true}
                      />
                      <InputRightElement>
                        <Tooltip label="Link kopieren">
                          <IconButton
                            aria-label="Copy"
                            onClick={onCopy}
                            icon={<Icon as={hasCopied ? Check : Clipboard} />}
                          />
                        </Tooltip>
                      </InputRightElement>
                    </InputGroup>
                  </Flex>
                </Flex>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>Titel</FormLabel>
                    <Input
                      focusBorderColor="brand.500"
                      placeholder="Spontaner Live-Kurs"
                      {...form.register('title')}
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>Wie lang soll der Kurs gehen</FormLabel>
                    <RadioGroup colorScheme="brand" defaultValue="60">
                      <Stack direction="row">
                        <Radio value="30" {...form.register('duration')}>
                          30 Min
                        </Radio>
                        <Radio value="60" {...form.register('duration')}>
                          60 Min
                        </Radio>
                        <Radio value="90" {...form.register('duration')}>
                          90 Min
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </>
              )}
              <Flex justify="flex-end">
                <Button variant="ghost" r={3} onClick={onClose}>
                  Abbrechen
                </Button>
                <Button colorScheme="brand" type="submit">
                  Kurs starten
                </Button>
              </Flex>
            </form>
          </ModalContent>
        </Modal>
      </>
    );
  };
